import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import React, { useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  getEndpointShieldHistory,
  IEndpointShieldHistoryItem,
  IEndpointShieldHistoryResponse,
} from '~/src/dash/api/endpoint-shield';

// Neo-Brutalism Light Mode Color Palette
const colors = {
  background: '#F8F9FA',
  cardBackground: '#FFFFFF',
  accentSalmon: '#FFA69E',
  accentPeriwinkle: '#B8B8FF',
  accentYellow: '#FEE72F',
  accentMint: '#98F5E1',
  textPrimary: '#1A1A2E',
  textSecondary: '#4A4A5A',
  border: '#E0E0E0',
  shadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  triggered: '#FF6B6B',
  notTriggered: '#4ECDC4',
  impressions: '#6366F1',
  requests: '#F59E0B',
  throttle: '#EF4444',
};

const useStyles = makeStyles({
  container: {
    backgroundColor: colors.background,
    padding: '24px',
    minHeight: '400px',
    borderRadius: '12px',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 800,
    fontSize: '28px',
    textTransform: 'uppercase',
    color: colors.textPrimary,
    letterSpacing: '1px',
  },
  subtitle: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    color: colors.textSecondary,
    marginTop: '4px',
  },
  bentoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCardMint: {
    borderLeft: `6px solid ${colors.accentMint}`,
  },
  statCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: colors.shadow,
    border: `2px solid ${colors.border}`,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
    },
  },
  statCardSalmon: {
    borderLeft: `6px solid ${colors.accentSalmon}`,
  },
  statCardPeriwinkle: {
    borderLeft: `6px solid ${colors.accentPeriwinkle}`,
  },
  statCardYellow: {
    borderLeft: `6px solid ${colors.accentYellow}`,
  },
  statLabel: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 700,
    fontSize: '11px',
    textTransform: 'uppercase',
    color: colors.textSecondary,
    letterSpacing: '1.5px',
    marginBottom: '8px',
  },
  statValue: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 800,
    fontSize: '32px',
    color: colors.textPrimary,
  },
  accordion: {
    backgroundColor: colors.cardBackground,
    borderRadius: '12px !important',
    boxShadow: colors.shadow,
    border: `2px solid ${colors.border}`,
    marginBottom: '12px',
    overflow: 'hidden',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 0 12px 0',
    },
  },
  accordionSummary: {
    backgroundColor: colors.background,
    borderBottom: `2px solid ${colors.border}`,
    minHeight: '64px',
    '&.Mui-expanded': {
      minHeight: '64px',
    },
  },
  accordionSummaryContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    margin: '12px 0',
    '&.Mui-expanded': {
      margin: '12px 0',
    },
  },
  accordionDetails: {
    padding: '24px',
    backgroundColor: colors.cardBackground,
  },
  chipContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  chip: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '13px',
    backgroundColor: colors.cardBackground,
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    height: '32px',
    '& .MuiChip-label': {
      paddingLeft: '12px',
      paddingRight: '4px',
    },
  },
  chipCountry: {
    borderColor: colors.accentPeriwinkle,
    backgroundColor: '#F0F0FF',
  },
  chipAppBundle: {
    borderColor: colors.accentMint,
    backgroundColor: '#F0FFF8',
  },
  copyButton: {
    padding: '2px',
    marginLeft: '-4px',
    marginRight: '4px',
    '& svg': {
      fontSize: '14px',
      color: colors.textSecondary,
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: '20px',
    fontWeight: 700,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  badgeTriggered: {
    backgroundColor: '#FFE5E5',
    color: colors.triggered,
    border: `2px solid ${colors.triggered}`,
  },
  badgeNotTriggered: {
    backgroundColor: '#E5FFF9',
    color: colors.notTriggered,
    border: `2px solid ${colors.notTriggered}`,
  },
  chartContainer: {
    width: '100%',
    height: '350px',
  },
  summaryStats: {
    display: 'flex',
    gap: '24px',
    marginLeft: 'auto',
    marginRight: '16px',
  },
  summaryStatItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  summaryStatLabel: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '10px',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  summaryStatValue: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 700,
    fontSize: '14px',
    color: colors.textPrimary,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    gap: '16px',
  },
  loadingText: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  errorContainer: {
    backgroundColor: '#FFE5E5',
    border: `2px solid ${colors.triggered}`,
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
  },
  errorText: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    color: colors.triggered,
  },
  emptyContainer: {
    backgroundColor: colors.cardBackground,
    border: `2px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '48px',
    textAlign: 'center',
  },
  emptyText: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 600,
    fontSize: '16px',
    color: colors.textSecondary,
  },
  emptySubtext: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    color: colors.textSecondary,
    marginTop: '8px',
  },
});

interface IEndpointShieldViewProps {
  siteId: string;
  publisherId: string;
}

interface IGroupedData {
  hash: string;
  country: string;
  appBundle: string;
  hasTriggered: boolean;
  totalRequests: number;
  totalImpressions: number;
  currentThrottle: number;
  chartData: IChartDataPoint[];
}

interface IChartDataPoint {
  date: string;
  impressions: number;
  endpointRequests: number;
  throttlePercentage: number;
}

export const EndpointShieldView: React.FC<IEndpointShieldViewProps> = ({
  siteId,
  publisherId,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<IEndpointShieldHistoryResponse | null>(null);
  const [groupedData, setGroupedData] = useState<IGroupedData[]>([]);
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState<boolean>(false);

  const handleCopy = (text: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopySnackbarOpen(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const [response, err] = await getEndpointShieldHistory(siteId, publisherId);

      if (err) {
        setError(err.message || 'Failed to fetch endpoint shield history');
        setLoading(false);
        return;
      }

      if (response) {
        setData(response);
        const grouped = groupDataByHash(response.data);
        setGroupedData(grouped);
        // Expand first panel by default if there's data
        if (grouped.length > 0) {
          setExpandedPanels([grouped[0].hash]);
        }
      }
      setLoading(false);
    };

    if (siteId) {
      fetchData();
    }
  }, [siteId, publisherId]);

  const groupDataByHash = (items: IEndpointShieldHistoryItem[]): IGroupedData[] => {
    const hashMap = new Map<string, IEndpointShieldHistoryItem[]>();

    // Group items by hash
    items.forEach((item) => {
      const existing = hashMap.get(item.hash) || [];
      existing.push(item);
      hashMap.set(item.hash, existing);
    });

    // Convert to grouped data array
    const result: IGroupedData[] = [];

    hashMap.forEach((hashItems, hash) => {
      // Sort items by date
      const sortedItems = hashItems.sort(
        (a, b) => new Date(a.ts_date).getTime() - new Date(b.ts_date).getTime(),
      );

      const firstItem = sortedItems[0];
      const lastItem = sortedItems[sortedItems.length - 1];
      const hasTriggered = sortedItems.some((item) => item.triggered);
      const totalRequests = sortedItems.reduce((sum, item) => sum + item.endpoint_requests, 0);
      const totalImpressions = sortedItems.reduce((sum, item) => sum + item.impressions, 0);
      const currentThrottle = lastItem.throttle_percentage;

      // Create chart data points
      const chartData: IChartDataPoint[] = sortedItems.map((item) => ({
        date: formatDateForChart(item.ts_date),
        impressions: item.impressions,
        endpointRequests: item.endpoint_requests,
        throttlePercentage: item.throttle_percentage,
      }));

      result.push({
        hash,
        country: firstItem.country,
        appBundle: firstItem.app_bundle,
        hasTriggered,
        totalRequests,
        totalImpressions,
        currentThrottle,
        chartData,
      });
    });

    // Sort by triggered status first, then by total requests
    return result.sort((a, b) => {
      if (a.hasTriggered !== b.hasTriggered) {
        return a.hasTriggered ? -1 : 1;
      }
      return b.totalRequests - a.totalRequests;
    });
  };

  const formatDateForChart = (dateStr: string): string => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const handleAccordionChange = (hash: string) => (_: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpandedPanels((prev) =>
      isExpanded ? [...prev, hash] : prev.filter((h) => h !== hash),
    );
  };

  const calculateOverallStats = () => {
    const uniqueHashes = groupedData.length;
    const triggeredCount = groupedData.filter((g) => g.hasTriggered).length;
    const totalRequests = groupedData.reduce((sum, g) => sum + g.totalRequests, 0);

    return { uniqueHashes, triggeredCount, totalRequests };
  };

  if (loading) {
    return (
      <Grid className={classes.container}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} style={{ color: colors.accentPeriwinkle }} />
          <Typography className={classes.loadingText}>Loading Shield Data...</Typography>
        </div>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid className={classes.container}>
        <div className={classes.errorContainer}>
          <Typography className={classes.errorText}>{error}</Typography>
        </div>
      </Grid>
    );
  }

  if (!data || groupedData.length === 0) {
    return (
      <Grid className={classes.container}>
        <div className={classes.header}>
          <Typography className={classes.title}>Endpoint Shield History</Typography>
          <Typography className={classes.subtitle}>Last 7 days of blocking activity</Typography>
        </div>
        <div className={classes.emptyContainer}>
          <Typography className={classes.emptyText}>No Shield Activity</Typography>
          <Typography className={classes.emptySubtext}>
            No endpoint shield blocking events recorded in the last 7 days for this channel.
          </Typography>
        </div>
      </Grid>
    );
  }

  const stats = calculateOverallStats();

  return (
    <Grid className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.title}>Endpoint Shield History</Typography>
        <Typography className={classes.subtitle}>
          {data.start_date} to {data.end_date}
        </Typography>
      </div>

      {/* Bento Grid Stats */}
      <div className={classes.bentoGrid}>
        <Paper className={`${classes.statCard} ${classes.statCardPeriwinkle}`} elevation={0}>
          <Typography className={classes.statLabel}>Unique Combinations</Typography>
          <Typography className={classes.statValue}>{stats.uniqueHashes}</Typography>
        </Paper>

        <Paper className={`${classes.statCard} ${classes.statCardSalmon}`} elevation={0}>
          <Typography className={classes.statLabel}>Triggered</Typography>
          <Typography className={classes.statValue}>{stats.triggeredCount}</Typography>
        </Paper>

        <Paper className={`${classes.statCard} ${classes.statCardYellow}`} elevation={0}>
          <Typography className={classes.statLabel}>Total Requests</Typography>
          <Typography className={classes.statValue}>{formatNumber(stats.totalRequests)}</Typography>
        </Paper>

        <Paper className={`${classes.statCard} ${classes.statCardMint}`} elevation={0}>
          <Typography className={classes.statLabel}>Requests Blocked Yesterday</Typography>
          <Typography className={classes.statValue}>WIP</Typography>
        </Paper>
      </div>

      {/* Grouped Accordions */}
      {groupedData.map((group) => (
        <Accordion
          key={group.hash}
          className={classes.accordion}
          expanded={expandedPanels.includes(group.hash)}
          onChange={handleAccordionChange(group.hash)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ content: classes.accordionSummaryContent }}
          >
            <div className={classes.chipContainer}>
              <Chip
                className={`${classes.chip} ${classes.chipCountry}`}
                label={group.country}
                size="small"
                deleteIcon={
                  <Tooltip title="Copy country">
                    <IconButton className={classes.copyButton} size="small">
                      <FileCopyOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                }
                onDelete={handleCopy(group.country)}
              />
              <Chip
                className={`${classes.chip} ${classes.chipAppBundle}`}
                label={group.appBundle}
                size="small"
                deleteIcon={
                  <Tooltip title="Copy app bundle">
                    <IconButton className={classes.copyButton} size="small">
                      <FileCopyOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                }
                onDelete={handleCopy(group.appBundle)}
              />
            </div>
            <span
              className={`${classes.badge} ${
                group.hasTriggered ? classes.badgeTriggered : classes.badgeNotTriggered
              }`}
            >
              {group.hasTriggered ? 'Triggered' : 'Monitored'}
            </span>
            <div className={classes.summaryStats}>
              <div className={classes.summaryStatItem}>
                <span className={classes.summaryStatLabel}>Requests</span>
                <span className={classes.summaryStatValue}>{formatNumber(group.totalRequests)}</span>
              </div>
              <div className={classes.summaryStatItem}>
                <span className={classes.summaryStatLabel}>Impressions</span>
                <span className={classes.summaryStatValue}>{formatNumber(group.totalImpressions)}</span>
              </div>
              <div className={classes.summaryStatItem}>
                <span className={classes.summaryStatLabel}>Allowed Requests</span>
                <span className={classes.summaryStatValue}>{group.currentThrottle}%</span>
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            {/* Chart */}
            <div className={classes.chartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={group.chartData}
                  margin={{ top: 20, right: 60, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: colors.textSecondary, fontWeight: 600 }}
                    axisLine={{ stroke: colors.border }}
                    tickLine={{ stroke: colors.border }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 12, fill: colors.textSecondary, fontWeight: 600 }}
                    axisLine={{ stroke: colors.border }}
                    tickLine={{ stroke: colors.border }}
                    tickFormatter={(value) => formatNumber(value)}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12, fill: colors.throttle, fontWeight: 600 }}
                    axisLine={{ stroke: colors.throttle }}
                    tickLine={{ stroke: colors.throttle }}
                    tickFormatter={(value) => `${value}%`}
                    domain={[0, 100]}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: colors.cardBackground,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '8px',
                      boxShadow: colors.shadow,
                    }}
                    labelStyle={{
                      fontWeight: 700,
                      color: colors.textPrimary,
                      textTransform: 'uppercase',
                      fontSize: '12px',
                    }}
                    formatter={(value: number, name: string) => [
                      name === 'Allowed %' ? `${value}%` : formatNumber(value),
                      name,
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    wrapperStyle={{
                      paddingTop: '16px',
                      fontFamily: '"Inter", "Roboto", sans-serif',
                      fontWeight: 600,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                    }}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="endpointRequests"
                    fill={colors.requests}
                    radius={[4, 4, 0, 0]}
                    name="Endpoint Requests"
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="impressions"
                    stroke={colors.impressions}
                    strokeWidth={3}
                    dot={{ fill: colors.impressions, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    name="Impressions"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="throttlePercentage"
                    stroke={colors.throttle}
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    dot={{ fill: colors.throttle, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    name="Allowed %"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </AccordionDetails>
        </Accordion>
      ))}

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Grid>
  );
};
